import { DateRangeProps, formatDay, StringMap } from "../components";

export type BrightnessType = "light" | "dark" | "any";

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const passMinLength = 6;

export const uppercaseRegex = /[A-Z]/;

export const lowercaseRegex = /[a-z]/;

export const numberRegex = /[0-9]/;

export const specialCharacterRegex = /[!@#$%^&*(),.?":{}|<>]/;

export const dateFormat = "DD/MM/YYYY";

export const resetObjectValues = <T extends Record<string, string>>(
  obj: T
): { [K in keyof T]: string } => {
  const entries = Object.keys(obj).map((k) => [k, ""]);
  return Object.fromEntries(entries) as { [K in keyof T]: string };
};

interface DataItem {
  [key: string]: any;
}

// Filtros cuyos valores vienen de un desplegable de opciones fijas (Edit.Selection):
// deben compararse por igualdad exacta, nunca por subcadena ("active" es subcadena de "inactive").
const EXACT_MATCH_FILTER_KEYS = [
  "status",
  "operation",
  "result",
  "brandName",
  "fiTerminalSelect",
  "retailer",
  "establishment",
];

// Filtros cuyo id no coincide con el nombre real del campo en el dato (por evitar duplicar
// el mismo id de campo en dos <Edit.*> distintos montados a la vez, ver ReportFilters.tsx).
// Sin alias, un filtro cuyo id no existe en el dato no filtra nada (ver filterData).
const FILTER_FIELD_ALIASES: Record<string, string> = {
  retailerSelect: "retailer",
  fiTerminalSelect: "fiTerminal",
  idprod: "idProd",
  product: "productName",
  result: "operationResult",
};

// Campos que contienen una fecha ISO y se comparan por día con un filtro "DD/MM/YYYY".
const DATE_FILTER_FIELDS = ["date", "expirationDate"];

const matchesDateFilter = (isoValue: string, filterValueDMY: string): boolean => {
  const [day, month, year] = filterValueDMY.split("/").map(Number);
  if (!day || !month || !year) return true;

  const itemDate = new Date(isoValue);
  if (isNaN(itemDate.getTime())) return false;

  return (
    itemDate.getDate() === day &&
    itemDate.getMonth() + 1 === month &&
    itemDate.getFullYear() === year
  );
};

export function filterData<T extends DataItem>(
  data: T[],
  filters: StringMap
): T[] {
  // Obtenemos keys cuyo filtro no está vacío (tras trim)
  const activeKeys = Object.keys(filters).filter(
    (k) => (filters[k] ?? "").trim() !== ""
  );

  // Si no hay filtros activos, devolvemos todo
  if (activeKeys.length === 0) return data.slice();

  return data.filter((item) =>
    activeKeys.every((key) => {
      const rawFilterValue = filters[key];
      // Si no hay filtro (por seguridad) o está vacío, permitimos todo
      if (!rawFilterValue || rawFilterValue.trim() === "") return true;

      // El filtro de fecha compara por día, no por subcadena de texto
      if (key === "date") {
        const dateField = DATE_FILTER_FIELDS.find((field) => field in item);
        if (!dateField || !item[dateField]) return true;
        return matchesDateFilter(item[dateField], rawFilterValue.trim());
      }

      const field = FILTER_FIELD_ALIASES[key] ?? key;

      // Si el item no tiene esa key, no lo excluimos (según tu regla)
      if (!(field in item)) return true;

      const itemVal = String(item[field] ?? "").toUpperCase();
      // Permitimos múltiples valores en el filtro separados por coma (OR)
      const patterns = rawFilterValue
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => p.toUpperCase());

      // Si no hay patterns válida, no filtra (devuelve true)
      if (patterns.length === 0) return true;

      if (EXACT_MATCH_FILTER_KEYS.includes(key)) {
        return patterns.some((pat) => itemVal === pat);
      }

      // Si cualquiera de los patterns se encuentra dentro del valor del item -> OK
      return patterns.some((pat) => itemVal.includes(pat));
    })
  );
}

export const randomHexColor = (type: BrightnessType): string => {
  let digits;

  if (type === "dark") {
    digits = [4, 5, 6, 7, 8, 9, "a", "b", "c", "e"];
  } else if (type === "light") {
    digits = [7, 8, 9, "a", "b", "c", "d", "e", "f"];
  } else {
    digits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, "a", "b", "c", "d", "e", "f"];
  }

  let hexCode = "#";

  while (hexCode.length < 7) {
    hexCode += digits[Math.round(Math.random() * digits.length)];
  }

  return hexCode;
};

export const random20DarkColors = Array.from({ length: 20 }, () =>
  randomHexColor("dark")
);

export const formatDateToLocal = (dateStr: string): string => {
  const dateParts = dateStr.split("T")[0].split("-");
  return formatDay(`${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`);
};

// ISO del back -> "DD/MM/YYYY HH:mm:ss", tomando la hora tal cual viene (sin
// convertir de zona horaria), igual que en los informes manuales.
export const formatDateTimeToLocal = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  const [datePart, timePart = ""] = dateStr.split("T");
  const [year, month, day] = datePart.split("-");
  const time = timePart.slice(0, 8);
  return `${day}/${month}/${year}${time ? ` ${time}` : ""}`;
};

export const formatAmount = (value?: number | null): string =>
  value === null || value === undefined
    ? ""
    : `${value.toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}€`;

// "DD/MM/YYYY" -> "YYYY-MM-DD", para enviar como query param al backend.
export const toISODateFromDMY = (value: string): string | undefined => {
  const [day, month, year] = value.split("/");
  if (!day || !month || !year) return undefined;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

// Construye un query string ("?a=1&b=2") a partir de un objeto, omitiendo
// valores vacíos/undefined. Devuelve "" (sin "?") si no queda nada que enviar.
export const buildQueryString = (
  params: Record<string, string | undefined>
): string => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value.length > 0) {
      usp.set(key, value);
    }
  });
  const query = usp.toString();
  return query.length > 0 ? `?${query}` : "";
};

// Rango por defecto "del 1 del mes actual a hoy", en formato DD/MM/YYYY.
// Construido con los getters de Date (no Date.toLocaleDateString(), cuyo orden
// día/mes depende del locale del navegador y rompía el RangePicker con "Invalid Date").
export const getCurrentMonthToTodayRange = (): DateRangeProps => {
  const today = new Date();
  const pad = (value: number): string => String(value).padStart(2, "0");
  const month = pad(today.getMonth() + 1);
  const year = String(today.getFullYear());

  return {
    startDate: `01/${month}/${year}`,
    endDate: `${pad(today.getDate())}/${month}/${year}`,
  };
};
